import { getAlbums, deleteAlbum } from '@/app/services/albums/albums';
import * as Actions from '@/app/store/actions';
import { CircularProgress } from '@material-ui/core';
import { ExpandMore as ChevronDownIcon } from '@material-ui/icons';
import Pagination from '@material-ui/lab/Pagination';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import GradientButton from '@shared/components/GradientButton';
import CreateAlbumModal from './CreateAlbumModal';

const GalleryAlbums = ({ filters, createModalOpen, setCreateModalOpen, searchQuery }) => {
	const [albums, setAlbums] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [sortBy, setSortBy] = useState('updated');
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const fetchAlbums = useCallback(async () => {
		try {
			setLoading(true);
			const params = { page, sort: sortBy };

			if (filters.room_id?.length > 0) {
				params.room_id = filters.room_id.map((r) => r.value);
			}
			if (filters.type && filters.type.value !== 'all') {
				params.type = filters.type.value;
			}
			if (searchQuery.trim()) {
				params.search = searchQuery.trim();
			}

			const res = await getAlbums(params);
			const data = res.data.data || res.data || [];
			setAlbums(data);
			setTotalPages(res.data?.meta?.last_page || res.data?.last_page || 1);
		} catch (error) {
			dispatch(Actions.showMessage({ message: 'Failed to fetch albums', variant: 'error' }));
			setAlbums([]);
		} finally {
			setLoading(false);
		}
	}, [page, filters, sortBy, searchQuery]);

	useEffect(() => {
		fetchAlbums();
	}, [fetchAlbums]);

	// Reset page when filters/search/sort change
	useEffect(() => {
		setPage(1);
	}, [filters.room_id, filters.type, sortBy, searchQuery]);

	const handleDeleteAlbum = async (e, albumId) => {
		e.stopPropagation();
		if (!window.confirm('Are you sure you want to delete this album?')) return;
		try {
			await deleteAlbum(albumId);
			dispatch(Actions.showMessage({ message: 'Album deleted', variant: 'success' }));
			fetchAlbums();
		} catch (error) {
			const message = error?.response?.data?.message || 'Failed to delete album';
			dispatch(Actions.showMessage({ message, variant: 'error' }));
		}
	};

	return (
		<div>
			{/* Sort Bar */}
			<div className="gallery-sort-bar">
				<div className="flex items-center gap-12">
					<span className="sort-label">Sort by:</span>
					<select
						className="border-none bg-transparent text-14 text-gray-600 cursor-pointer outline-none"
						onChange={(e) => setSortBy(e.target.value)}
						value={sortBy}
					>
						<option value="updated">most recently updated</option>
						<option value="name">name</option>
					</select>
				</div>
			</div>

			{/* Loading */}
			{loading ? (
				<div className="flex justify-center py-64">
					<CircularProgress size={40} />
				</div>
			) : albums.length === 0 ? (
				<div className="flex flex-col items-center py-64">
					<p className="text-gray-500 text-18 mb-8">No albums found</p>
					<p className="text-gray-400 text-14 mb-20">
						Create your first album to organize photos and videos.
					</p>
					<GradientButton
						onClick={() => setCreateModalOpen(true)}
						size="small"
						type="button"
						variant="light-blue"
					>
						<PlusCircleIcon className="w-24" />
						Create Album
					</GradientButton>
				</div>
			) : (
				<>
					{/* Albums Grid */}
					<div className="gallery-albums-grid">
						{albums.map((album) => (
							<div
								className="gallery-album-card"
								key={album.id}
								onClick={() => navigate(`/memories/albums/${album.id}`)}
							>
								<div className="album-card-body">
									<div className="flex items-start justify-between mb-12">
										<div>
											<h3>{album.name}</h3>
											{/* Hidden for now - may come back */}
											<div className="album-meta" style={{ display: 'none' }}>
												<span>
													{album.room
														? `sharing with ${album.room.name} families`
														: 'sharing with all families'}
												</span>
												<ChevronDownIcon style={{ fontSize: 14, marginLeft: 2, verticalAlign: 'middle' }} />
											</div>
										</div>
										{album.type === 'manual' && (
											<button
												className="text-gray-400 hover:text-red-500 text-14 cursor-pointer bg-transparent border-none"
												onClick={(e) => handleDeleteAlbum(e, album.id)}
												title="Delete album"
												type="button"
											>
												&#x2715;
											</button>
										)}
									</div>

									{/* Thumbnail Strip */}
									<div className="album-thumbnails">
										{Array.from({ length: 6 }).map((_, index) => {
											const media = album.media?.[index];
											const thumb = media?.thumb || media?.link;
											return thumb ? (
												<img alt="" key={index} src={thumb} />
											) : (
												<div className="thumbnail-placeholder" key={index} />
											);
										})}
									</div>

									<div className="album-counts">
										{album.photo_count || 0} photo{album.photo_count !== 1 ? 's' : ''} &bull; {album.video_count || 0} video{album.video_count !== 1 ? 's' : ''}
									</div>
								</div>
							</div>
						))}
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="gallery-pagination">
							<Pagination
								boundaryCount={1}
								color="primary"
								count={totalPages}
								onChange={(_, value) => setPage(value)}
								page={page}
								shape="rounded"
								siblingCount={0}
							/>
						</div>
					)}
				</>
			)}

			<CreateAlbumModal
				onAlbumCreated={fetchAlbums}
				open={createModalOpen}
				setOpen={setCreateModalOpen}
			/>
		</div>
	);
};

export default GalleryAlbums;
